import { WellLogsWidget as GoWellLogsWidget, useBitmapFont } from '@go-widgets/well-logs-widget';
import type { WellIndexType, WidgetStateParams } from '@go-widgets/well-logs-widget';
import inter18ptRegularFntUrl from '@go-widgets/well-logs-widget/dist/assets/fonts/Inter_18pt-Regular.fnt?url';
import inter18ptRegularPngUrl from '@go-widgets/well-logs-widget/dist/assets/fonts/Inter_18pt-Regular.png?url';
import {
  EditLightBig as EditIcon,
  FullArrowLight as FullIcon,
  ExportLight as ExportIcon,
  ReduceLight as MinimizeIcon,
  Refresh2Light,
} from '@repo/go-icons';
import { Button, ConfigContext, Loader, Switch, Tooltip } from '@repo/go-ui';
import type { VideoBroadcastStateParams } from '@repo/go-widgets';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEV_SETTINGS_PAGE_AVAILABLE } from 'src/api/consts';
import type { TCreateWidgetOptions } from 'src/entities/tab/TabEntity';
import { useService } from 'src/packages/di';
import { IS_PORTABLE_DEVICE } from 'src/packages/shared/consts/isPortableDevice';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import type { WellLogsWidgetEntity } from './WellLogsWidget.entity';
import styles from './WellLogsWidget.module.scss';
import { WellLogsWidgetStore } from './WellLogsWidget.store';

type WrapperProps = {
  entity: WellLogsWidgetEntity;
  options: TWidgetRendererOptions;
  onCreateWellLogsWidget(
    wellIndexType: WellIndexType,
    wellId: number | null,
    stateParams: Partial<WidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): void;
  onCreateVideobroadcastWidget(
    wellId: number | null,
    stateParams: Partial<VideoBroadcastStateParams> | null,
    options?: TCreateWidgetOptions,
  ): void;
};

type Props = {
  store: WellLogsWidgetStore;
  entity: WellLogsWidgetEntity;
  options: TWidgetRendererOptions;
};

export const WellLogsWidgetWrapper = observer(function WellLogsWidgetWrapper({
  entity,
  options,
  onCreateWellLogsWidget,
  onCreateVideobroadcastWidget,
}: WrapperProps) {
  const [store, setStore] = useState<WellLogsWidgetStore | null>(null);

  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const _store = storeService.getStore<WellLogsWidgetEntity, WellLogsWidgetStore>(entity);

    if (_store) {
      setStore(_store);
    } else {
      const newStore = new WellLogsWidgetStore(
        entity,
        options.groupSelectStore,
        onCreateWellLogsWidget,
        onCreateVideobroadcastWidget,
        options.wellListEntity,
      );

      storeService.setStore<WellLogsWidgetEntity, WellLogsWidgetStore>(entity, newStore);

      setStore(newStore);
    }
  }, [
    setStore,
    storeService,
    entity,
    onCreateWellLogsWidget,
    onCreateVideobroadcastWidget,
    options.groupSelectStore,
    store,
    options.wellListEntity,
  ]);

  if (!store) return null;

  return <WellLogsWidget store={store} entity={entity} options={options} />;
});

export const WellLogsWidget = observer(function WellLogsWidget({ entity, options, store }: Props) {
  const globalConfig = useContext(ConfigContext);
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);
  const { loaded: bitmapFontLoaded, error: bitmapFontError } = useBitmapFont({
    fontName: 'Inter_18pt-Regular',
    fntUrl: inter18ptRegularFntUrl,
    pngUrl: inter18ptRegularPngUrl,
  });

  const {
    setGroupForWellLogs,
    enableEditMode,
    handleExport,
    handleReloadWellStructure,
    exitFullScreen,
    isReady,
    isExitFullscreenFetching,
    effect,
    progress,
  } = store;

  const { t } = useTranslation();

  const { wellId, isEditMode, isFullscreen, state, setFullscreen, wellboreName } = entity;

  useEffect(effect, [effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;

  return (
    <div className={styles.wellLogsWidgetWrapper} ref={(ref) => setWrapperElement(ref)}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForWellLogs}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        progress={progress}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => {
          if (!isReady) return null;

          return (
            <>
              {DEV_SETTINGS_PAGE_AVAILABLE && (
                <Switch checked={store.curveRenderingMode === 'mesh'} onChange={() => store.toggleCurveRenderMode()} />
              )}
              {!isEditMode && (
                <Tooltip title={t('common:Widget.reloadStructure')} delay={400}>
                  <Button
                    onClick={handleReloadWellStructure}
                    variant="flat"
                    icon={<Refresh2Light className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {!isEditMode && !IS_PORTABLE_DEVICE && (
                <Tooltip title={t('common:Widget.edit')} delay={400}>
                  <Button
                    onClick={enableEditMode}
                    variant="flat"
                    icon={<EditIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              <Tooltip title={t('common:Widget.export')} delay={400}>
                <Button
                  onClick={handleExport}
                  variant="flat"
                  icon={<ExportIcon className={styles.icon} />}
                  className={styles.button}
                />
              </Tooltip>
              {!isFullscreen && (
                <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                  <Button
                    onClick={() => setFullscreen(true)}
                    variant="flat"
                    icon={<FullIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {isFullscreen && (
                <Tooltip title={t('common:Widget.collapse')} delay={400}>
                  <Button
                    onClick={() => exitFullScreen(globalConfig)}
                    variant="flat"
                    icon={<MinimizeIcon className={styles.icon} />}
                    disabled={isExitFullscreenFetching}
                    className={styles.button}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />
      {wrapperElement &&
        (state ? (
          <div className={styles.wrapper}>
            {!bitmapFontLoaded || bitmapFontError ? (
              <Loader size="l" />
            ) : (
              <GoWellLogsWidget widgetState={state} externalContainer={wrapperElement} />
            )}
          </div>
        ) : (
          <NoWellChosen />
        ))}
    </div>
  );
});
